import React from 'react'
import request from '@/modules/base/util/request'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import config from '@/config'
import lang from '@/lang'
import Tooltip from '@mui/material/Tooltip'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'

import { toast } from '@/modules/base/components/toast/toastConfig'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import IconArrowRight from '@assets/images/icons/arrowRight.svg'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    gap: 20,
  },
  alignCenter: {
    alignItems: 'center',
  },
  input: {
    width: 445,

    '& input': {
      background: theme.backgroundColor,
      [theme.devices.mobile]: {
        background: theme.white,
      },
    },
    [theme.devices.mobile]: {
      width: '100%',
    },
  },
  tooltip: {
    fontSize: 11,
  },
  button: {
    background: `${theme.backgroundColor} !important`,
    border: theme.border,
    '& svg': {
      fill: `${theme.black} !important`,
    },
    [theme.devices.mobile]: {
      background: theme.white,
    },
  },
  buttonIconOnly: {
    width: 45,
  },
  active: {
    background: theme.backgroundColor,
  },
  checkIcon: {
    fill: theme.white,
  },
  submit: {
    padding: '0 10px',
    '$active &': {
      color: theme.white,
    },
  },
}))

const CouponCodeInput = (props) => {
  const {
    onCouponCodeValidate,
    period,
    subscriptionType,
    subscriptionTypes,
    disabled,
    disabledHint,
    placeholder,
    className,
    value,
    errorMessage,
    onInputChange,
    onErrorChange,
  } = props
  const classes = useStyles()

  const validateCode = async() => {
    if (disabled) return

    const validate = async(type, subPeriod) => {
      try {
        const response = await request(`${config.resourceHost}subscription/validateCoupon`,
          {
            auth: true,
            data: {
              subscriptionType: type,
              code: value,
              period: props.period || subPeriod,
            },
          })
        onCouponCodeValidate && onCouponCodeValidate(response.body.data, type)
        onInputChange('')
        onErrorChange('')
        toast.showToast({ message: lang.upgradeModal.couponCodeValid })
        return true
      } catch {
        return false
      }
    }

    const typesToValidate = subscriptionTypes.length ? subscriptionTypes : [{ name: subscriptionType, period }]
    for (const { name, period } of typesToValidate) {
      // returns after just one valid code is found valid
      if (await validate(name, period)) return
    }

    onErrorChange(lang.upgradeModal.couponCodeInvalid)
    toast.showToast({ type: 'error', message: lang.upgradeModal.couponCodeInvalid })
  }

  const input = (
    <div className={clsx(classes.container, className)}>
      <TextInput
        name='couponCode'
        placeholder={placeholder || lang.upgradeModal.couponCodePlaceholder}
        value={value}
        errorMessage={errorMessage}
        onChange={onInputChange}
        customClass={classes.input}
        autoComplete='new-password'
        readOnly={disabled}
        disabled={disabled}
      />
      <ButtonIconSquare
        iconClass={classes.button}
        className={clsx(classes.container, classes.alignCenter, classes.button, value.length > 0 && classes.active)}
        icon={IconArrowRight}
        onClick={validateCode}
        disabled={disabled}
      />
    </div>
  )

  return (
    <>
      {disabled && disabledHint
        ? (
          <Tooltip
            title={disabledHint}
            classes={{ tooltip: classes.tooltip }}
            placement='top'>
            {input}
          </Tooltip>)
        : input}
    </>
  )
}

CouponCodeInput.propTypes = {
  onCouponCodeValidate: PropTypes.func,
  period: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  disabledHint: PropTypes.string,
  subscriptionType: PropTypes.string,
  subscriptionTypes: PropTypes.array,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  errorMessage: PropTypes.string,
  onInputChange: PropTypes.func,
  onErrorChange: PropTypes.func,
}

CouponCodeInput.defaultProps = {
  disabled: false,
  subscriptionTypes: [],
  placeholder: null,
  className: null,
}

export default CouponCodeInput
